import { BigNumber, toBigNumber } from '@injectivelabs/utils/big-number'
import { ONE_IN_BIG_NUMBER, ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { LEVERAGE_STEPS } from '@/app/utils/constants'

export function calculateNotional(params: {
  price: BigNumber
  quantity: BigNumber
}): BigNumber {
  return params.price.times(params.quantity)
}

export function calculateQuantityFromNotional(params: {
  price: BigNumber
  notional: BigNumber
}): BigNumber {
  if (params.price.isZero()) {
    return ZERO_IN_BIG_NUMBER
  }

  return params.notional.div(params.price)
}

export function calculateFeeAmount(params: {
  value: BigNumber
  feeRate: BigNumber
}): BigNumber {
  return params.value.times(params.feeRate)
}

export function calculateNotionalBeforeFee(params: {
  feeRate: BigNumber
  notional: BigNumber
}): BigNumber {
  return params.notional.div(ONE_IN_BIG_NUMBER.plus(params.feeRate))
}

export function calculateSlippagePrice(params: {
  isBuy: boolean
  price: BigNumber
  slippageTolerance: BigNumber
}): BigNumber {
  if (params.isBuy) {
    return params.price.times(ONE_IN_BIG_NUMBER.plus(params.slippageTolerance))
  }

  return params.price.times(ONE_IN_BIG_NUMBER.minus(params.slippageTolerance))
}

export function calculateMinimumNotional(params: {
  price: BigNumber
  priceDecimals: number
  quantityTensMultiplier: number
}): BigNumber {
  const minQuantityInBigNumber = toBigNumber(10).exponentiatedBy(
    params.quantityTensMultiplier
  )

  const minNotionalInBigNumber = calculateNotional({
    price: params.price,
    quantity: toBigNumber(minQuantityInBigNumber)
  })

  return toBigNumber(
    minNotionalInBigNumber.dp(params.priceDecimals, BigNumber.ROUND_UP)
  )
}

export function calculateImrMaxLeverage(params: {
  initialMarginRatio: number | BigNumber
}): BigNumber {
  return calculateMarginRatioMaxLeverage({
    marginRatio: params.initialMarginRatio
  })
}

export function calculateMarginRatioMaxLeverage(params: {
  marginRatio: number | BigNumber
}): BigNumber {
  if (toBigNumber(params.marginRatio).isZero()) {
    return ONE_IN_BIG_NUMBER
  }

  const maxLeverage = ONE_IN_BIG_NUMBER.div(params.marginRatio).dp(
    0,
    BigNumber.ROUND_DOWN
  )

  const closestStep = LEVERAGE_STEPS.findLast((step) => maxLeverage.gte(step))

  return toBigNumber(closestStep ?? maxLeverage)
}

export function calculateMarkPriceMaxLeverage(params: {
  isBuy: boolean
  referencePrice: BigNumber
  executionPrice: BigNumber
  marginRatio: number | BigNumber
}): BigNumber {
  const maxLeverageByMarginRatio = calculateMarginRatioMaxLeverage({
    marginRatio: params.marginRatio
  })

  if (params.executionPrice.isZero() || params.referencePrice.isZero()) {
    return maxLeverageByMarginRatio
  }

  const marginRatio = toBigNumber(params.marginRatio)

  const minMarginPerUnit = params.isBuy
    ? params.referencePrice
        .times(marginRatio.minus(1))
        .plus(params.executionPrice)
    : params.referencePrice
        .times(marginRatio.plus(1))
        .minus(params.executionPrice)

  if (minMarginPerUnit.lte(0)) {
    return maxLeverageByMarginRatio
  }

  return params.executionPrice.div(minMarginPerUnit)
}

export function calculatePnl(params: {
  isBuy: boolean
  quantity: BigNumber
  markPrice: BigNumber
  entryPrice: BigNumber
}): BigNumber {
  return params.quantity
    .times(params.markPrice.minus(params.entryPrice))
    .times(params.isBuy ? 1 : -1)
}

export function calculatePnlRatio(params: {
  pnl: BigNumber
  margin: BigNumber
}): BigNumber {
  if (params.margin.isZero()) {
    return ZERO_IN_BIG_NUMBER
  }

  return params.pnl.div(params.margin)
}
